import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, User, X } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';
import './testimonial-carousel.css';

const TestimonialCarousel = ({ testimonials }) => {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Initialize Embla Carousel with options and plugins
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'center',
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
      direction: 'ltr',
      startIndex: 0,
      skipSnaps: false,
    },
    [Autoplay({ delay: 6000, stopOnInteraction: true })]
  );
  
  // Track all slides to apply inactive styling
  const [slideStates, setSlideStates] = React.useState([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    
    // Update the selected index
    setSelectedIndex(newIndex);
    
    // Update the slide states to apply inactive styles
    const numSlides = emblaApi.slideNodes().length;
    const newSlideStates = Array.from({ length: numSlides }, (_, i) => i !== newIndex);
    setSlideStates(newSlideStates);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    // Initialize slide states
    const numSlides = emblaApi.slideNodes().length;
    const initialIndex = emblaApi.selectedScrollSnap();
    const initialSlideStates = Array.from({ length: numSlides }, (_, i) => i !== initialIndex);
    setSlideStates(initialSlideStates);
    
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  return (
    <div className="max-w-7xl mx-auto relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className={`flex-[0_0_70%] min-w-0 pl-[20px] embla-slide ${
                slideStates[index] ? 'embla-slide-inactive' : ''
              }`}
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-xl flex flex-col md:flex-row relative h-full">
                {/* Left column - gold background with user info */}
                <div className="md:w-1/3 bg-[#b99764] p-8 flex items-center relative">
                  <div className="text-center w-full relative z-20">
                    <div 
                      className="w-24 h-24 bg-white/90 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-200"
                      onClick={() => {
                        setSelectedTestimonial(testimonial);
                        setIsPopupOpen(true);
                      }}
                    >
                      {testimonial.image ? (
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/data/testimonial/images/default-avatar.jpg';
                            if (!e.target.src.includes('default-avatar.jpg')) {
                              e.target.parentNode.innerHTML = `<User className="h-12 w-12 text-[#b99764]" />`;
                            }
                          }}
                        />
                      ) : (
                        <User className="h-12 w-12 text-[#b99764]" />
                      )}
                    </div>
                    <h4 
                      className="testimonial-name font-bold text-white text-xl mb-1 cursor-pointer hover:underline"
                      onClick={() => {
                        setSelectedTestimonial(testimonial);
                        setIsPopupOpen(true);
                      }}
                    >
                      {testimonial.name}
                    </h4>
                    <p className="text-white/80 text-sm mb-2">
                      {/* Mahasiswa S1<br /> */}
                      {testimonial.position}
                    </p>
                  </div>
                </div>
                
                {/* Right column - testimonial content */}
                <div className="md:w-2/3 p-8 relative">
                  <span className="text-5xl text-[#b99764] font-serif leading-none absolute top-6 left-8 z-20">"</span>
                  <blockquote className="text-lg italic text-gray-700 mb-4 pt-6 relative z-20">
                    <p className="relative z-20">{testimonial.excerpt || 'Tidak ada testimoni'}</p>
                  </blockquote>
                  <div className="flex items-center justify-end mt-4 relative z-20">
                    <div className="h-1 w-16 bg-[#b99764] mr-2"></div>
                    <p className="text-sm font-medium text-[#b99764]">{testimonial.year}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Navigation Buttons */}
      <div className="absolute left-4 top-[200px] md:top-[160px] z-30">
        <button 
          onClick={scrollPrev}
          className="w-12 h-12 rounded-full bg-[#1a2639]/90 hover:bg-[#b99764] flex items-center justify-center text-white transition-colors shadow-lg"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      </div>
      
      <div className="absolute right-4 top-[200px] md:top-[160px] z-30">
        <button 
          onClick={scrollNext}
          className="w-12 h-12 rounded-full bg-[#1a2639]/90 hover:bg-[#b99764] flex items-center justify-center text-white transition-colors shadow-lg"
          aria-label="Next testimonial"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
      
      {/* Indicator Dots */}
      <div className="flex justify-center mt-8 gap-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              selectedIndex === index
                ? "bg-[#b99764] scale-110" 
                : "bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>

      {/* Testimonial Popup */}
      {isPopupOpen && selectedTestimonial && (
        <div className="testimonial-popup active" onClick={(e) => {
          if (e.target.classList.contains('testimonial-popup')) {
            setIsPopupOpen(false);
          }
        }}>
          <div className="testimonial-popup-content">
            <button 
              className="testimonial-popup-close"
              onClick={() => setIsPopupOpen(false)}
              aria-label="Close popup"
            >
              <X />
            </button>
            
            <div className="w-60 h-60 mx-auto mb-6 rounded-full overflow-hidden">
              {selectedTestimonial.image ? (
                <img 
                  src={selectedTestimonial.image} 
                  alt={selectedTestimonial.name}
                  className="testimonial-popup-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/data/testimonial/images/default-avatar.jpg';
                    if (!e.target.src.includes('default-avatar.jpg')) {
                      e.target.parentNode.innerHTML = `<User className="h-24 w-24 text-[#b99764]" />`;
                    }
                  }}
                />
              ) : (
                <User className="h-24 w-24 text-[#b99764]" />
              )}
            </div>
            
            <h3 className="testimonial-popup-name">{selectedTestimonial.name}</h3>
            <p className="text-center text-gray-600 mb-4">
              {/* Mahasiswa S1<br /> */}
              {selectedTestimonial.position}
            </p>
            
            {/* <div className="testimonial-popup-excerpt text-gray-700 italic">
              "{selectedTestimonial.excerpt || 'Tidak ada testimoni singkat'}"
            </div> */}
            
            <div className="testimonial-popup-body mt-6 border-t pt-6">
              {/* <h4 className="text-lg font-semibold text-gray-800 mb-4">Testimonial Lengkap:</h4> */}
              <div className="prose prose-lg max-w-none text-gray-700">
                {selectedTestimonial.body || 'Tidak ada testimoni lengkap'}
              </div>
            </div>
            
            <div className="flex items-center justify-end mt-6">
              <div className="h-1 w-16 bg-[#b99764] mr-2"></div>
              <p className="text-sm font-medium text-[#b99764]">{selectedTestimonial.year}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialCarousel;